/**
 * useOrDraft — état brouillon d'un OR pour la page d'édition "Facture à émettre".
 *
 * Reprend la mécanique éprouvée d'OrDetail.jsx : `or` = état serveur (référence),
 * `draft` = état local édité. `saveAll()` synchronise par diff (PUT /or + CRUD lignes).
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const OR_FIELDS = [
  "type_client_tarif", "km_depose", "notes_client", "notes_internes",
  "rabais_global_pct", "rabais_global_chf", "entete_libre", "pied_libre",
];
const LINE_FIELDS = [
  "libelle", "quantite", "prix_unitaire_ht", "tva_pct",
  "rabais_pct", "rabais_chf", "unite", "type_ligne", "categorie_compta",
  "categorie_mo",
];

// iter127 — Une ligne est "nouvelle" si elle n'a pas d'id serveur ou si elle
// possède un identifiant local (`_localId` ajouté par LignesEditor partagé).
const isNewLigne = (d) => !d.id || String(d.id).startsWith("_") || !!d._localId;

const intervenantsEqual = (a, b) => {
  const A = a || []; const B = b || [];
  if (A.length !== B.length) return false;
  for (let i = 0; i < A.length; i++) {
    if ((A[i].mecanicien_id || "") !== (B[i].mecanicien_id || "")) return false;
    if (Math.abs((parseFloat(A[i].heures) || 0) - (parseFloat(B[i].heures) || 0)) > 0.001) return false;
  }
  return true;
};

export function computeTotauxFromDraft(d) {
  let sousTotalHt = 0, rabaisLignes = 0;
  for (const l of (d?.lignes || [])) {
    if ((l.type_ligne || "libre") === "texte") continue;
    const brut = (parseFloat(l.quantite) || 0) * (parseFloat(l.prix_unitaire_ht) || 0);
    const rab = brut * ((parseFloat(l.rabais_pct) || 0) / 100) + (parseFloat(l.rabais_chf) || 0);
    sousTotalHt += brut;
    rabaisLignes += Math.min(rab, brut);
  }
  const htApresLignes = Math.max(0, sousTotalHt - rabaisLignes);
  const rg = htApresLignes * ((parseFloat(d?.rabais_global_pct) || 0) / 100) + (parseFloat(d?.rabais_global_chf) || 0);
  const rabaisGlobal = Math.min(rg, htApresLignes);
  const totalHt = Math.max(0, htApresLignes - rabaisGlobal);
  const ratio = htApresLignes > 0 ? totalHt / htApresLignes : 0;
  let tva = 0;
  for (const l of (d?.lignes || [])) {
    if ((l.type_ligne || "libre") === "texte") continue;
    const brut = (parseFloat(l.quantite) || 0) * (parseFloat(l.prix_unitaire_ht) || 0);
    const rab = Math.min(brut * ((parseFloat(l.rabais_pct) || 0) / 100) + (parseFloat(l.rabais_chf) || 0), brut);
    const netLigne = (brut - rab) * ratio;
    tva += netLigne * ((parseFloat(l.tva_pct) || 0) / 100);
  }
  return {
    sous_total_ht: sousTotalHt,
    rabais_lignes_total: rabaisLignes,
    rabais_global: rabaisGlobal,
    total_ht: totalHt,
    tva,
    total_ttc: totalHt + tva,
  };
}

export default function useOrDraft(id) {
  const [or, setOr] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const fetchOr = useCallback(async () => {
    try {
      const { data } = await api.get(`/or/${id}`);
      setOr(data);
      setDraft(JSON.parse(JSON.stringify(data)));
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchOr(); }, [fetchOr]);

  const isDirty = useMemo(() => {
    if (!or || !draft) return false;
    for (const k of OR_FIELDS) {
      if ((or[k] ?? "") !== (draft[k] ?? "")) return true;
    }
    if (!intervenantsEqual(or.intervenants, draft.intervenants)) return true;
    const ol = or.lignes || [];
    const dl = draft.lignes || [];
    if (ol.length !== dl.length) return true;
    for (let i = 0; i < dl.length; i++) {
      const d = dl[i], s = ol[i];
      if (!s) return true;
      if (isNewLigne(d)) return true;
      if (d.id !== s.id) return true;
      for (const k of LINE_FIELDS) {
        if ((d[k] ?? "") !== (s[k] ?? "")) return true;
      }
    }
    return false;
  }, [or, draft]);

  const dirtyCount = useMemo(() => {
    if (!or || !draft) return 0;
    let n = 0;
    for (const k of OR_FIELDS) {
      if ((or[k] ?? "") !== (draft[k] ?? "")) n++;
    }
    if (!intervenantsEqual(or.intervenants, draft.intervenants)) n++;
    const ol = or.lignes || [];
    const dl = draft.lignes || [];
    n += dl.filter(isNewLigne).length;
    const draftIds = new Set(dl.filter((d) => !isNewLigne(d)).map((d) => d.id));
    n += ol.filter((s) => !draftIds.has(s.id)).length;
    for (const d of dl) {
      if (isNewLigne(d)) continue;
      const s = ol.find((x) => x.id === d.id);
      if (!s) continue;
      if (LINE_FIELDS.some((k) => (d[k] ?? "") !== (s[k] ?? ""))) n++;
    }
    return n;
  }, [or, draft]);

  // === Édition locale des lignes ===
  const updateLigne = (ligne) => {
    setDraft((d) => ({
      ...d,
      lignes: (d.lignes || []).map((l) => (l.id === ligne.id ? { ...l, ...ligne } : l)),
    }));
  };

  const addLigne = (type_ligne = "libre", presets = {}) => {
    const tempId = `_new_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const newLigne = {
      id: tempId,
      or_id: id,
      type_ligne,
      libelle: presets.libelle || "",
      quantite: 1,
      prix_unitaire_ht: presets.prix_unitaire_ht || 0,
      tva_pct: 8.1,
      rabais_pct: 0,
      rabais_chf: 0,
      unite: presets.unite || "piece",
      article_id: presets.article_id || null,
      position: (draft?.lignes || []).length + 1,
      total_ht: 0,
      ...presets,
    };
    setDraft((d) => ({ ...d, lignes: [...(d.lignes || []), newLigne] }));
  };

  const deleteLigne = (lid) => {
    setDraft((d) => ({ ...d, lignes: (d.lignes || []).filter((l) => l.id !== lid) }));
  };

  const reorderLignes = (from, to) => {
    setDraft((d) => {
      const next = [...(d.lignes || [])];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return { ...d, lignes: next };
    });
  };

  const updateGlobalRabais = (field, value) => {
    setDraft((d) => ({
      ...d,
      [field === "pct" ? "rabais_global_pct" : "rabais_global_chf"]: parseFloat(value) || 0,
    }));
  };

  // === Enregistrer : sync serveur par diff ===
  const saveAll = useCallback(async () => {
    if (!or || !draft) return true;
    setSaving(true);
    try {
      const orChanged = OR_FIELDS.some((k) => (or[k] ?? "") !== (draft[k] ?? ""))
        || !intervenantsEqual(or.intervenants, draft.intervenants);
      if (orChanged) {
        await api.put(`/or/${id}`, {
          client_id: draft.client_id,
          vehicule_id: draft.vehicule_id,
          rdv_id: draft.rdv_id,
          type_client_tarif: draft.type_client_tarif,
          km_depose: draft.km_depose,
          notes_client: draft.notes_client,
          notes_internes: draft.notes_internes,
          photos_depose: draft.photos_depose,
          rabais_global_pct: draft.rabais_global_pct,
          rabais_global_chf: draft.rabais_global_chf,
          entete_libre: draft.entete_libre || null,
          pied_libre: draft.pied_libre || null,
          intervenants: (draft.intervenants || []).filter((x) => x.mecanicien_id && (parseFloat(x.heures) || 0) > 0),
        });
      }
      const draftIds = new Set((draft.lignes || []).filter((d) => !isNewLigne(d)).map((d) => d.id));
      for (const s of (or.lignes || [])) {
        if (!draftIds.has(s.id)) {
          await api.delete(`/or/lignes/${s.id}`);
        }
      }
      const draftLignes = draft.lignes || [];
      for (let pos = 0; pos < draftLignes.length; pos++) {
        const d = draftLignes[pos];
        const isNew = isNewLigne(d);
        const payload = {
          type_ligne: d.type_ligne,
          libelle: d.libelle,
          quantite: d.quantite,
          prix_unitaire_ht: d.prix_unitaire_ht,
          tva_pct: d.tva_pct ?? d.tva_taux ?? 8.1,
          rabais_pct: d.rabais_pct,
          rabais_chf: d.rabais_chf,
          unite: d.unite,
          article_id: d.article_id || null,
          categorie_compta: d.categorie_compta || null,
          categorie_mo: d.categorie_mo || null,
          position: pos + 1,
        };
        if (isNew) {
          await api.post(`/or/${id}/lignes`, payload);
        } else {
          const s = (or.lignes || []).find((x) => x.id === d.id);
          if (s) {
            const lineChanged = LINE_FIELDS.some((k) => (d[k] ?? "") !== (s[k] ?? ""));
            const posChanged = (s.position ?? 0) !== pos + 1;
            if (lineChanged || posChanged) {
              await api.put(`/or/lignes/${d.id}`, payload);
            }
          }
        }
      }
      await fetchOr();
      toast.success("Modifications enregistrées");
      setSaving(false);
      return true;
    } catch (e) {
      setSaving(false);
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
      return false;
    }
  }, [or, draft, id, fetchOr]);

  const cancelAll = useCallback(() => {
    if (!isDirty) return;
    if (!window.confirm(`Annuler ${dirtyCount} modification(s) non enregistrée(s) ?`)) return;
    setDraft(JSON.parse(JSON.stringify(or)));
    toast.info("Modifications annulées");
  }, [isDirty, dirtyCount, or]);

  const totaux = isDirty ? computeTotauxFromDraft(draft) : or?.totaux;

  return {
    or, setOr, draft, setDraft, loading, notFound, saving,
    fetchOr, isDirty, dirtyCount, saveAll, cancelAll, totaux,
    updateLigne, addLigne, deleteLigne, reorderLignes, updateGlobalRabais,
  };
}
